/**
 * BrightSign development shim — fakes the BSDeviceInfo and BSMessagePort
 * globals that the real BrightSign firmware injects into the HTML zone.
 *
 * Activation rules:
 *  - `?bsshim=1` in the URL query string, OR
 *  - `window.location.hostname === 'localhost'` (Chrome dev server).
 *
 * When inactive, this module is a complete no-op — no globals are set,
 * no code runs. That's important because bootstrap.js unconditionally
 * requires this file; if the shim were side-effecting on every load it
 * would break the web-browser / Android paths that share the same
 * index.js entry.
 *
 * The shim mirrors the surface area exposed by BrightSign OS 9.x:
 *  - BSDeviceInfo: synchronous globals (model, serialNumber, firmwareVersion,
 *    deviceFamily, bootVersion, etc.) populated by autorun.brs before the
 *    HTML zone loads.
 *  - BSMessagePort: bidirectional message bus between the HTML zone and
 *    BrightScript (PostBSMessage → BrightScript, onbsmessage ← BrightScript).
 *
 * For tests, the shim exports `forceActivate()` / `deactivate()` helpers
 * so Jest can install and tear down the mocks deterministically.
 */

/**
 * Default values returned by the shim. These match the feature set of
 * a real BrightSign XD235 (entry-tier XD series) running OS 9.0.142.
 * Tests can override these by calling forceActivate() with custom values.
 */
const DEFAULT_DEVICE_INFO = Object.freeze({
  model: 'XD235',
  serialNumber: 'XXXXX',
  firmwareVersion: '9.0.142',
  deviceFamily: 'panther',
  bootVersion: '9.0.0',
});

/**
 * Detect whether the shim should install itself based on URL / hostname.
 * Returns true when the bundle is running locally (Chrome dev) or the
 * URL explicitly asks for the shim via `?bsshim=1`.
 */
function shouldActivateAutomatically() {
  if (typeof window === 'undefined' || !window.location) return false;
  // Force-off hook for tests that need to probe the inactive behaviour.
  if (window.__TRILL_BS_SHIM_DISABLED__) return false;
  try {
    const params = new URLSearchParams(window.location.search || '');
    if (params.get('bsshim') === '1') return true;
  } catch (_) {
    // URLSearchParams may not exist in very old JSDOM; fall through to
    // the hostname check.
  }
  const hostname = window.location.hostname || '';
  if (hostname === 'localhost' || hostname === '127.0.0.1') return true;
  return false;
}

/**
 * Install the shim globals. Safe to call multiple times — each write is
 * idempotent and the second call will overwrite the previous handler
 * (which is what tests want).
 *
 * Returns the installed BSDeviceInfo object so callers can mutate it if
 * they need to simulate a different player.
 */
function forceActivate(overrides) {
  if (typeof window === 'undefined') return null;

  const deviceInfo = Object.assign({}, DEFAULT_DEVICE_INFO, overrides || {});
  window.BSDeviceInfo = deviceInfo;

  // BSMessagePort exposes PostBSMessage (JS → BrightScript) and an
  // onbsmessage property (BrightScript → JS). The real implementation is
  // native, but for Chrome dev we just log so the developer can see the
  // messages the React bundle is emitting.
  if (!window.BSMessagePort || !window.BSMessagePort.__isTrillShim) {
    const messagePort = {
      __isTrillShim: true,
      /**
       * PostBSMessage outbox — every payload the bundle sends to the host
       * is captured here so tests can inspect the forwarded commands. The
       * DeviceCommandDispatcher tests assert on this array to verify the
       * correct BSMessagePort message was sent for a given deviceCommand.
       */
      __outbox: [],
      /**
       * PostBSMessage — called by the React bundle to send data to the
       * BrightScript host. In the shim we log the payload and append it
       * to the outbox so tests can assert on the forwarded commands.
       */
      PostBSMessage(payload) {
        try {
          // eslint-disable-next-line no-console
          console.debug('[BSShim] PostBSMessage', payload);
        } catch (_) {
          // console may be suppressed
        }
        try {
          this.__outbox.push(payload);
        } catch (_) {
          // outbox may be frozen by a caller; swallow to stay fire-and-forget
        }
      },
      /**
       * onbsmessage — real BrightSign fires this when BrightScript sends
       * a message to JS. In the shim it's a no-op that tests can replace
       * by assigning a function.
       */
      onbsmessage: null,
      /**
       * __emitFromBs — test-only helper used to simulate a message arriving
       * from the BrightScript host. Prefers onbsmessage (the canonical
       * BrightSign API) and falls back to the trillboards:bs-message
       * CustomEvent if no onbsmessage handler is installed. Matches the
       * real player which fires onbsmessage exactly once per message —
       * bootstrap.html's CustomEvent dispatch is a downstream effect of
       * the installed onbsmessage handler, so firing both here would
       * double-deliver to any listener that subscribes to both paths.
       */
      __emitFromBs(payload) {
        if (typeof this.onbsmessage === 'function') {
          try {
            this.onbsmessage(payload);
          } catch (_) {
            // handler errors must not abort the emit loop
          }
          return;
        }
        try {
          if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
            window.dispatchEvent(
              new CustomEvent('trillboards:bs-message', { detail: payload })
            );
          }
        } catch (_) {
          // CustomEvent may not exist in some sandboxes; ignore.
        }
      },
    };
    window.BSMessagePort = messagePort;
  }

  window.__TRILL_BS_SHIM_ACTIVE__ = true;
  return deviceInfo;
}

/**
 * Tear down the shim — used by regression tests to assert that the
 * non-BrightSign paths behave identically when BSDeviceInfo is absent.
 */
function deactivate() {
  if (typeof window === 'undefined') return;
  try {
    delete window.BSDeviceInfo;
  } catch (_) {
    window.BSDeviceInfo = undefined;
  }
  try {
    delete window.BSMessagePort;
  } catch (_) {
    window.BSMessagePort = undefined;
  }
  try {
    delete window.__TRILL_BS_SHIM_ACTIVE__;
  } catch (_) {
    window.__TRILL_BS_SHIM_ACTIVE__ = undefined;
  }
}

// Auto-activate based on URL / hostname. This is the runtime path that
// bootstrap.js relies on — in tests we skip this by setting
// window.__TRILL_BS_SHIM_DISABLED__ = true BEFORE require()ing this file.
if (shouldActivateAutomatically()) {
  forceActivate();
}

export {
  forceActivate,
  deactivate,
  shouldActivateAutomatically,
  DEFAULT_DEVICE_INFO,
};
